import { menuItem } from '@/ui/components/menu/style';
import { popup } from '@/ui/popup';
import { cn } from '@/ui/utils';
import { Menu as MenuPrimitive } from '@base-ui/react/menu';
import { ArrowDownIcon, ArrowRight01Icon, Tick02Icon } from '@hugeicons-pro/core-stroke-rounded';
import { Icon } from '@/ui/components/icon';
import { ScrollArea } from '@/ui/components/scroll-area';
import { VariantProps } from 'cva';

export const Menu = MenuPrimitive.Root;
export const Submenu = MenuPrimitive.SubmenuRoot;
export const MenuTrigger = MenuPrimitive.Trigger;
export const MenuGroup = MenuPrimitive.Group;
export const MenuRadioGroup = MenuPrimitive.RadioGroup;
export const MenuPortal = MenuPrimitive.Portal;
export const MenuBackdrop = MenuPrimitive.Backdrop;
export const MenuPositioner = MenuPrimitive.Positioner;
export const MenuArrow = MenuPrimitive.Arrow;

export function MenuGroupLabel({
  className,
  children,
  ...props
}: React.ComponentProps<typeof MenuPrimitive.GroupLabel>) {
  return (
    <MenuPrimitive.GroupLabel className={cn(popup.groupLabel, className)} {...props}>
      {children}
    </MenuPrimitive.GroupLabel>
  );
}

export function MenuTriggerIcon({
  className,
  ...props
}: Omit<React.ComponentProps<typeof Icon>, 'icon'>) {
  return (
    <Icon
      className={cn(
        'my-auto size-4! shrink-0 in-data-[size=xs]:-mr-1! in-data-[size=sm]:-mr-1! -mr-1.5! ml-auto',
        className,
      )}
      data-slot="menu-trigger-icon"
      {...props}
      icon={ArrowDownIcon}
    />
  );
}

interface Props
  extends React.ComponentProps<typeof MenuPrimitive.Item>,
    VariantProps<typeof menuItem> {}

export function MenuItem({ className, variant, children, ...props }: Props) {
  return (
    <MenuPrimitive.Item className={cn(menuItem({ variant, className }))} {...props}>
      {children}
    </MenuPrimitive.Item>
  );
}

export function MenuCheckboxItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof MenuPrimitive.CheckboxItem>) {
  return (
    <MenuPrimitive.CheckboxItem
      className={cn(menuItem({ variant: 'checkbox' }), 'group', className)}
      {...props}
    >
      <span className="shrink-0 size-4 rounded-sm grid place-items-center border border-surface-6 hover:border-primary-10 transition-colors has-data-checked:border-primary-10 has-data-checked:text-white has-data-checked:bg-primary-10 mr-1">
        <MenuPrimitive.CheckboxItemIndicator className={'invisible group-data-checked:visible'}>
          <Icon icon={Tick02Icon} className={'size-3.5 text-white'} strokeWidth={2} />
        </MenuPrimitive.CheckboxItemIndicator>
      </span>
      {children}
    </MenuPrimitive.CheckboxItem>
  );
}

export function MenuRadioItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof MenuPrimitive.RadioItem>) {
  return (
    <MenuPrimitive.RadioItem
      className={cn(menuItem({ variant: 'checkbox' }), 'group', className)}
      {...props}
    >
      {children}
      <span className="shrink-0 invisible ml-auto group-hover:visible has-data-checked:visible">
        <MenuPrimitive.RadioItemIndicator className={'invisible group-data-checked:visible'}>
          <Icon icon={Tick02Icon} className={'size-5'} />
        </MenuPrimitive.RadioItemIndicator>
      </span>
    </MenuPrimitive.RadioItem>
  );
}

// export function MenuCheckboxItemIndicator() {
//   return (
//     <MenuPrimitive.CheckboxItemIndicator className={'-mr-1 md:-mr-0.5 ml-auto'}>
//       <Icon icon={Tick02Icon} className={'size-5'} />
//     </MenuPrimitive.CheckboxItemIndicator>
//   );
// }

export function MenuCheckboxItemText({ className, ...props }: React.ComponentProps<'span'>) {
  return <span className={cn('col-start-2', className)} {...props} />;
}

export function MenuScrollArea({
  className,
  children,
  viewportRef,
}: {
  className?: string;
  children: React.ReactNode;
  viewportRef?: React.Ref<HTMLDivElement>;
}) {
  return (
    <ScrollArea
      className="flex flex-col rounded-[inherit]"
      viewportClassName="scroll-py-(--popup-padding)"
      viewportRef={viewportRef}
    >
      <div className={cn('p-(--popup-padding)', className)}>{children}</div>
    </ScrollArea>
  );
}

export function MenuPopup({
  className,
  children,
  sideOffset = 4,
  finalFocus,
  scrollArea = true,
  positionerClassName,
  ...props
}: React.ComponentProps<typeof MenuPrimitive.Positioner> & {
  finalFocus?: React.ComponentProps<typeof MenuPrimitive.Popup>['finalFocus'];
  scrollArea?: boolean;
  positionerClassName?: string;
}) {
  return (
    <MenuPortal>
      <MenuBackdrop />
      <MenuPositioner sideOffset={sideOffset} className={positionerClassName} {...props}>
        <MenuPrimitive.Popup
          finalFocus={finalFocus}
          className={cn(
            popup.base,
            popup.transition,
            'flex flex-col max-h-[min(var(--available-height),20rem)] min-w-36 text-base',
            className,
          )}
        >
          {scrollArea ? <MenuScrollArea>{children}</MenuScrollArea> : children}
        </MenuPrimitive.Popup>
      </MenuPositioner>
    </MenuPortal>
  );
}

export function MenuSubmenuTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof MenuPrimitive.SubmenuTrigger>) {
  return (
    <MenuPrimitive.SubmenuTrigger
      className={cn(menuItem({ variant: 'default', className }))}
      {...props}
    >
      {children}
      <Icon icon={ArrowRight01Icon} className="ml-auto shrink-0 size-4! -mr-1.5" />
    </MenuPrimitive.SubmenuTrigger>
  );
}

export function MenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof MenuPrimitive.Separator>) {
  return <MenuPrimitive.Separator className={cn(popup.separator, className)} {...props} />;
}
